import { useEffect, useState } from 'react';

import { marketplaceModule } from '../../../../api/modules/marketplace.module';

/**
 * The facet DATA, separate from the facet PANELS.
 *
 * Same split, and the same reason, as `filterModel.js`: two surfaces now draw
 * the same facets from the same three endpoints — the desktop rail
 * (`MarketplaceFilters`) and the mobile filter sheet (`MarketplaceFilterSheet`)
 * — and the fetches, the cancellation and the "rarities are game-specific"
 * rule are identical on both. Copying the effects into the sheet would have
 * been ~100 lines of duplicated async, and the copy that drifts first is
 * always the one nobody is looking at.
 *
 * Every hook here returns `{ data, loading }` and nothing else. No filter state
 * lives in this file: a hook that also owned the selection would put two
 * sources of truth behind one panel.
 */

/** The TCG list. Fetched once per mount, it is the root of every other facet. */
export function useTcgCategories() {
  const [tcgCategories, setTcgCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchTCGCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await marketplaceModule.getTCGCategories();
        if (cancelled) return;
        if (response.success) {
          setTcgCategories(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching TCG categories:', error);
      } finally {
        if (!cancelled) setLoadingCategories(false);
      }
    };

    fetchTCGCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  return { tcgCategories, loadingCategories };
}

/**
 * The rarity list for one TCG.
 *
 * Rarities are game-specific, so there is no meaningful cross-game list. With
 * no TCG chosen this fetches NOTHING and returns an empty list, and the panel
 * that renders it is expected to say why rather than show an empty group.
 */
export function useTcgRarities(tcgCategoryId) {
  const [rarities, setRarities] = useState([]);
  const [loadingRarities, setLoadingRarities] = useState(false);
  const [loadedFor, setLoadedFor] = useState(null);

  useEffect(() => {
    if (!tcgCategoryId) {
      setRarities([]);
      return undefined;
    }

    let cancelled = false;
    const fetchRarities = async () => {
      try {
        setLoadingRarities(true);
        const response = await marketplaceModule.getTCGCategoryRarities(tcgCategoryId);
        if (!cancelled) {
          setRarities(response.success ? response.data || [] : []);
        }
      } catch (error) {
        console.error('Error fetching TCG rarities:', error);
        if (!cancelled) setRarities([]);
      } finally {
        if (!cancelled) {
          setLoadingRarities(false);
          setLoadedFor(tcgCategoryId);
        }
      }
    };

    fetchRarities();
    return () => {
      cancelled = true;
    };
  }, [tcgCategoryId]);

  /* PENDING COUNTS AS LOADING. The fetch starts in an effect, which is one
     paint after the id changed, so a bare `loadingRarities` is false for that
     frame and the panel flashes "No rarities available for this game" over a
     game that has plenty. `loadedFor` is what the list currently describes. */
  return {
    rarities,
    loadingRarities: loadingRarities || (Boolean(tcgCategoryId) && tcgCategoryId !== loadedFor),
  };
}

/**
 * Every set in one TCG, for the set drill.
 *
 * Sets are language-agnostic, which is why this takes a TCG and nothing else.
 * Like the rarities, it refuses to fetch without one: the endpoint is per-TCG,
 * so "all sets" would mean fanning out one request per game to build a list
 * nobody can read anyway — there are hundreds of sets per game.
 */
export function useTcgCatalogs(tcgCategoryId) {
  const [catalogs, setCatalogs] = useState([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [loadedFor, setLoadedFor] = useState(null);

  useEffect(() => {
    if (!tcgCategoryId) {
      setCatalogs([]);
      return undefined;
    }

    let cancelled = false;
    const fetchCatalogs = async () => {
      try {
        setLoadingCatalogs(true);
        const response = await marketplaceModule.getTCGCategoryCatalogs(tcgCategoryId);
        if (!cancelled) {
          setCatalogs(response.success ? response.data || [] : []);
        }
      } catch (error) {
        console.error('Error fetching TCG catalogs:', error);
        if (!cancelled) setCatalogs([]);
      } finally {
        if (!cancelled) {
          setLoadingCatalogs(false);
          setLoadedFor(tcgCategoryId);
        }
      }
    };

    fetchCatalogs();
    return () => {
      cancelled = true;
    };
  }, [tcgCategoryId]);

  /* Same pending-is-loading rule as the rarities above, and it matters more
     here: the set panel is opened and the fetch begins in the same tick, so
     without it every drill into Set opens on "No sets listed for this game
     yet" before the list arrives. */
  return {
    catalogs,
    loadingCatalogs: loadingCatalogs || (Boolean(tcgCategoryId) && tcgCategoryId !== loadedFor),
  };
}

/**
 * The TCG name and set name behind a bare `catalog_group_id`.
 *
 * The marketplace can be entered from `/sets` with only the id in the URL, so
 * the applied set has to be resolved to something a human recognises before it
 * can be shown as a chip or a row value. There is no endpoint that takes an id
 * and returns the set, hence the search: the selected TCG first, because that
 * is one request and is right nearly always, then the rest.
 *
 * `tcgCategories` is passed in rather than fetched again so a mounted panel
 * reuses the list it already has.
 */
export function useCatalogGroupInfo(catalogGroupId, tcgCategoryId, tcgCategories) {
  const [catalogGroupInfo, setCatalogGroupInfo] = useState(null);
  const [loadingCatalogGroup, setLoadingCatalogGroup] = useState(false);

  useEffect(() => {
    const fetchCatalogGroupInfo = async () => {
      if (!catalogGroupId) {
        setCatalogGroupInfo(null);
        return;
      }

      try {
        setLoadingCatalogGroup(true);

        // Use cached categories if available, otherwise fetch
        let categories = tcgCategories;
        if (categories.length === 0) {
          const categoriesResponse = await marketplaceModule.getTCGCategories();
          if (!categoriesResponse.success) {
            return;
          }
          categories = categoriesResponse.data || [];
        }

        // If tcg_category_id is set, search only that category first
        const categoriesToSearch = tcgCategoryId
          ? categories.filter((cat) => cat.id === tcgCategoryId)
          : categories;

        for (const category of categoriesToSearch) {
          try {
            // Fetch every set for this TCG category (sets are language-agnostic).
            const catalogsResponse = await marketplaceModule.getTCGCategoryCatalogs(category.id);

            if (catalogsResponse.success && catalogsResponse.data) {
              const catalog = catalogsResponse.data.find(
                (cat) => cat.catalog_group_id === catalogGroupId,
              );

              if (catalog) {
                setCatalogGroupInfo({
                  tcgName: category.name || category.title || 'Unknown TCG',
                  setName: catalog.catalog_group || 'Unknown Set',
                });
                setLoadingCatalogGroup(false);
                return;
              }
            }
          } catch (err) {
            // Continue to next category if this one fails
            continue;
          }
        }

        // If not found and we only searched one category, search all categories
        if (tcgCategoryId && categoriesToSearch.length > 0) {
          for (const category of categories) {
            if (category.id === tcgCategoryId) continue;

            try {
              const catalogsResponse = await marketplaceModule.getTCGCategoryCatalogs(category.id);

              if (catalogsResponse.success && catalogsResponse.data) {
                const catalog = catalogsResponse.data.find(
                  (cat) => cat.catalog_group_id === catalogGroupId,
                );

                if (catalog) {
                  setCatalogGroupInfo({
                    tcgName: category.name || category.title || 'Unknown TCG',
                    setName: catalog.catalog_group || 'Unknown Set',
                  });
                  setLoadingCatalogGroup(false);
                  return;
                }
              }
            } catch (err) {
              continue;
            }
          }
        }

        // If not found, set to null
        setCatalogGroupInfo(null);
      } catch (error) {
        console.error('Error fetching catalog group info:', error);
        setCatalogGroupInfo(null);
      } finally {
        setLoadingCatalogGroup(false);
      }
    };

    fetchCatalogGroupInfo();
  }, [catalogGroupId, tcgCategoryId, tcgCategories]);

  return { catalogGroupInfo, loadingCatalogGroup };
}
